<script setup lang="ts">
import PortfolioAddressCell from '@/components/portfolio/PortfolioAddressCell.vue'
import PortfolioTableFilters from '@/components/portfolio/PortfolioTableFilters.vue'
import {
  averageYield,
  groupRowsByOwner,
  sumBy,
  toPortfolioRows,
  yieldClass,
  type PortfolioRow,
} from '@/components/portfolio/portfolioRows'
import type { ExtendedEnum } from '@/types'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { router } from '@inertiajs/vue3'
import { FilterMatchMode } from '@primevue/core/api'
import type { DataTableRowClickEvent, DataTableSortEvent } from 'primevue/datatable'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

const props = defineProps<{
  estateObjects: EstateObjectData[]
  estateObjectTypes: ExtendedEnum<EstateObjectType>[]
  missingFieldCounts: Record<string, number>
  activeFinancingRequestCounts: Record<string, number>
}>()

const { t, n } = useI18n()

const typeLabel = (type: EstateObjectType | null | undefined): string =>
  type ? t(`enums.estateObjectType.${type}`) : t('empty')

const rows = computed<PortfolioRow[]>(() =>
  toPortfolioRows(props.estateObjects, props.missingFieldCounts, props.activeFinancingRequestCounts, {
    typeLabel,
    withoutOwner: t('withoutOwner'),
  }),
)

const typeOptions = computed(() =>
  props.estateObjectTypes.map((option) => ({ value: option.id, label: typeLabel(option.id) })),
)

const filters = ref({
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  estateObjectType: { value: null as EstateObjectType | null, matchMode: FilterMatchMode.EQUALS },
})

const groupByOwner = ref(false)
const sortField = ref<string>('addressLine')
const sortOrder = ref<number>(1)

watch(groupByOwner, (enabled) => {
  if (enabled) {
    sortField.value = 'owner'
    sortOrder.value = 1
  }
})

const onSort = (event: DataTableSortEvent): void => {
  if (event.sortField !== 'owner') {
    groupByOwner.value = false
  }
}

const filteredRows = ref<PortfolioRow[]>(rows.value)

watch(rows, (next) => (filteredRows.value = next))

const rowsByOwner = computed(() => groupRowsByOwner(filteredRows.value))

const formatArea = (value: number | null): string => (value === null ? t('empty') : `${n(value)} m²`)

const formatEuro = (value: number | null): string => (value === null ? t('empty') : n(value, 'euros'))

const formatPercent = (value: number | null): string =>
  value === null ? t('empty') : `${n(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

const groupSummary = (owner: string): string => {
  const owned = rowsByOwner.value.get(owner) ?? []

  return t('groupSummary', {
    count: owned.length,
    units: sumBy(owned, (row) => row.units),
    value: formatEuro(sumBy(owned, (row) => row.price)),
    rent: formatEuro(sumBy(owned, (row) => row.rent)),
  })
}

const openDetail = (event: DataTableRowClickEvent): void =>
  router.visit(EstateObjectsController.show((event.data as PortfolioRow).id).url)

const groupHeaderFullWidth = {
  rowGroupHeaderCell: ({ instance }: { instance: { columnsLength: number } }) => ({
    colspan: instance.columnsLength,
  }),
}
</script>

<template>
  <div>
    <PortfolioTableFilters
      v-model:search="filters.global.value"
      v-model:type="filters.estateObjectType.value"
      v-model:group-by-owner="groupByOwner"
      :type-options="typeOptions"
      :shown-count="filteredRows.length"
      :total-count="rows.length" />

    <div class="bg-surface overflow-hidden">
      <DataTable
        v-model:filters="filters"
        v-model:sort-field="sortField"
        v-model:sort-order="sortOrder"
        :value="rows"
        data-key="id"
        sort-mode="single"
        :global-filter-fields="['addressLine', 'zipCode', 'city']"
        :row-group-mode="groupByOwner ? 'subheader' : undefined"
        :group-rows-by="groupByOwner ? 'owner' : undefined"
        row-hover
        class="finest-table"
        :pt="groupHeaderFullWidth"
        @sort="onSort"
        @value-change="filteredRows = $event as PortfolioRow[]"
        @row-click="openDetail">
        <template #empty>
          <p class="text-ink-3 py-8 text-center">{{ t('emptyResult') }}</p>
        </template>

        <template #groupheader="{ data }">
          <div class="flex flex-wrap items-baseline justify-between gap-2">
            <span>{{ (data as PortfolioRow).owner }}</span>
            <span class="text-ink-3 text-xs font-medium tracking-normal normal-case tabular-nums">
              {{ groupSummary((data as PortfolioRow).owner) }}
            </span>
          </div>
        </template>

        <Column field="addressLine" :header="t('columns.addressLine')" sortable>
          <template #body="{ data }">
            <PortfolioAddressCell :row="data as PortfolioRow" />
          </template>
          <template #footer>{{ t('total', { count: filteredRows.length }) }}</template>
        </Column>

        <Column
          field="estateObjectType"
          sort-field="estateObjectTypeLabel"
          :header="t('columns.estateObjectType')"
          sortable>
          <template #body="{ data }">
            <div class="max-w-40 truncate" :title="(data as PortfolioRow).estateObjectTypeLabel">
              {{ (data as PortfolioRow).estateObjectTypeLabel }}
            </div>
          </template>
        </Column>

        <Column field="units" :header="t('columns.units')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ (data as PortfolioRow).units ?? t('empty') }}</template>
          <template #footer>{{ sumBy(filteredRows, (row) => row.units) }}</template>
        </Column>

        <Column field="area" :header="t('columns.area')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatArea((data as PortfolioRow).area) }}</template>
          <template #footer>{{ formatArea(sumBy(filteredRows, (row) => row.area)) }}</template>
        </Column>

        <Column field="price" :header="t('columns.price')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatEuro((data as PortfolioRow).price) }}</template>
          <template #footer>{{ formatEuro(sumBy(filteredRows, (row) => row.price)) }}</template>
        </Column>

        <Column field="rent" :header="t('columns.rent')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatEuro((data as PortfolioRow).rent) }}</template>
          <template #footer>{{ formatEuro(sumBy(filteredRows, (row) => row.rent)) }}</template>
        </Column>

        <Column field="yield" :header="t('columns.yield')" sortable class="text-right tabular-nums">
          <template #body="{ data }">
            <span :class="yieldClass((data as PortfolioRow).yield)">
              {{ formatPercent((data as PortfolioRow).yield) }}
            </span>
          </template>
          <template #footer>{{ t('average', { value: formatPercent(averageYield(filteredRows)) }) }}</template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<style scoped src="../finest-table.css"></style>

<i18n>
{
  "de": {
    "empty": "—",
    "emptyResult": "Keine Objekte gefunden. Passe Suche oder Filter an.",
    "withoutOwner": "Ohne Eigentümer",
    "average": "Ø {value}",
    "total": "Summe ({count})",
    "groupSummary": "{count} Objekte · {units} Einh. · {value} · {rent}/M.",
    "columns": {
      "addressLine": "Objekt",
      "estateObjectType": "Art",
      "units": "Einh.",
      "area": "Fläche",
      "price": "Kaufpreis",
      "rent": "Ist-Miete p.M.",
      "yield": "Rendite"
    }
  },
  "en": {
    "empty": "—",
    "emptyResult": "No properties found. Adjust your search or filters.",
    "withoutOwner": "Without owner",
    "average": "Ø {value}",
    "total": "Total ({count})",
    "groupSummary": "{count} properties · {units} units · {value} · {rent}/mo.",
    "columns": {
      "addressLine": "Property",
      "estateObjectType": "Type",
      "units": "Units",
      "area": "Area",
      "price": "Purchase price",
      "rent": "Actual rent p.m.",
      "yield": "Yield"
    }
  }
}
</i18n>
